// Cash's words and links (finance redesign slice 4): the range picker's choices, a change from today signed and in
// words, the drawer a position row opens on /finance, the position's job states, job progress, cost notes and the
// composition's labels (cash where-we-stand, DD-054), and the agency cash form's check. No money is added up here: every
// figure is the server's (TD-102). On jobs (finance consolidation S9) a row opens the new model's drawers: a client's (its
// invoices) or a person's (their team invoices), never a contract month, Pay or a statement.
import type { BadgeTone } from "@/components/ui/Badge";
import type { ParamPatch } from "../hooks/useFinanceParams";
import { formatMoney } from "@/utils/currency";
import type { CashDays, CashOpens, JobProgress, PositionCost, PositionJobState } from "../types";

export const CASH_RANGES: readonly CashDays[] = [15, 30, 60, 90];
export const DEFAULT_DAYS: CashDays = 30;

/** The URL's `days`: one of the ranges, else 30. */
export function toDays(value: string | null): CashDays {
  return CASH_RANGES.find((days) => String(days) === value) ?? DEFAULT_DAYS;
}

/** A change from today, "+" above zero: the line's ticks without a level (A4). */
export const signedMoney = (minor: number): string => (minor > 0 ? `+${formatMoney(minor, "EUR")}` : formatMoney(minor, "EUR"));

/**
 * A day's change from today in words, without a level (A4): its size above or below where the agency stands today, or
 * "Same as today". A sign would read a day behind today that stood lower as a loss, when the agency gained since. The
 * strip's outer days and the line's hover.
 */
export function fromToday(minor: number): string {
  if (minor === 0) return "Same as today";
  return `${formatMoney(Math.abs(minor), "EUR")} ${minor > 0 ? "above" : "below"} today`;
}

/** The drawer a position row opens, as /finance's URL names it (slice 3's AgencyFinancePage). */
export function opensPatch(opens: CashOpens): ParamPatch {
  switch (opens.kind) {
    case "bill":
      return { modal: "expense", entry: opens.billId };
    case "expense":
      return { modal: "expense-item", tpl: opens.templateId };
    case "client":
      return { modal: "client", client: opens.clientId };
    case "person":
      return { modal: "person", person: opens.personId };
  }
}

/** A job's state on the position (DD-054): it names the job, and every state counts by the work done to today. */
export const JOB_STATES: Record<PositionJobState, { label: string; tone: BadgeTone }> = {
  sent: { label: "Sent", tone: "blue" },
  finished: { label: "Finished", tone: "amber" },
  running: { label: "Running", tone: "green" },
  waiting: { label: "Not on a month yet", tone: "gray" },
};

/** How far a job is: "3 of 31 days", "0 of 1 post". */
export function progressText(progress: JobProgress): string {
  const unit = progress.of === 1 ? progress.unit.slice(0, -1) : progress.unit;
  return `${progress.done} of ${progress.of} ${unit}`;
}

/** An expense's second line on the position: what kind it is, its client, its run-up and payments, paid ahead below zero,
 * and for a bill with no amount what it counts as. */
export function costNote(cost: PositionCost): string {
  const parts = [cost.kind === "repeating" ? "Repeating" : "Once"];
  if (cost.clientName) parts.push(cost.clientName);
  parts.push(`run up ${formatMoney(cost.runUpMinor, "EUR")}`, `paid ${formatMoney(cost.paidMinor, "EUR")}`);
  if (cost.amountMinor < 0) parts.push("paid ahead");
  if (cost.missing) parts.push(cost.runUpMinor > 0 ? "no amount yet, counted at its usual" : "no amount yet");
  return parts.join(" · ");
}

/** What an expense opens: a repeating one its expense, a one-off its bill. */
export function costOpens(cost: PositionCost): CashOpens {
  return cost.kind === "repeating" ? { kind: "expense", templateId: cost.expenseId } : { kind: "bill", billId: cost.expenseId };
}

/** Today is made of (DD-054): each part's words, in the spec's order. */
export const COMPOSITION = {
  title: "Today is made of",
  cash: "In the bank",
  clients: "Clients owe us, for work done to today",
  clientsNote: "still due on sent invoices and the work done on jobs not sent yet, plus costs and ads waiting to be rebilled",
  /** Clients below zero (decision 16): paid before the work was done. */
  clientsAhead: "Clients paid ahead",
  team: "We owe the team, for work done to today",
  /** DD-054: one label per basis. The Team tab's Owed now counts only claims owed; this counts the work done. */
  teamNote: "every claim on work done to today, owed now or not, paused jobs' left out",
  /** A person's claims paid out on a team invoice before their work is done, by the work not done yet (TD-081): apart from
   * the Team tab's Paid ahead, which holds a paid claim in full until it is owed. */
  teamAhead: "paid out ahead of the work",
  costs: "Costs run up and not paid",
  /** Costs below zero (decision 16): paid before they ran up. */
  costsAhead: "Costs paid ahead",
  total: "Where we stand today",
} as const;

/** J18: the card and the drawer ask for the agency's cash, not the bank's balance. */
export const AGENCY_CASH_HINT = "Your bank balance, less any money that is not the agency's.";

export interface AgencyCashCheck {
  dateError: string | null;
  blocked: boolean;
}

/** The agency cash form: a day, today or earlier, and an amount, below zero allowed (an overdrawn account). */
export function agencyCashCheck(asOf: string, amountMinor: number | null, today: string): AgencyCashCheck {
  const dateError = !asOf ? "Pick the day" : asOf > today ? "Pick today or an earlier day" : null;
  return { dateError, blocked: dateError !== null || amountMinor === null };
}
